import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import LoginPage from '@/pages/LoginPage';
import Dashboard from '@/pages/Dashboard';
import ProductsPage from '@/pages/ProductsPage';
import PurchaseOrdersPage from '@/pages/PurchaseOrdersPage';
import SalesPage from '@/pages/SalesPage';
import InvoicesPage from '@/pages/InvoicesPage';
import ReportsPage from '@/pages/ReportsPage';
import ClientsPage from '@/pages/ClientsPage';
import CreditSalesPage from '@/pages/CreditSalesPage';
import UsersPage from '@/pages/UsersPage';
import SettingsPage from '@/pages/SettingsPage';
import FixedExpensesPage from '@/pages/FixedExpensesPage';
import ReturnsInspectionPage from '@/pages/ReturnsInspectionPage';
import AppShell from '@/components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Currency Context
const CurrencyContext = createContext(null);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(7300);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }

    fetchExchangeRate();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error al obtener usuario', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const fetchExchangeRate = async () => {
    try {
      const response = await axios.get(`${API}/exchange-rate/`);
      setExchangeRate(response.data.usd_to_pyg);
    } catch (error) {
      console.error('Error al obtener tipo de cambio', error);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Error al iniciar sesión' };
    }
  };

  const register = async (email, password, name, role) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Error al registrarse' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const toggleCurrency = () => {
    setCurrency(prev => prev === 'USD' ? 'PYG' : 'USD');
    toast.success(`Moneda cambiada a ${currency === 'USD' ? 'PYG' : 'USD'}`);
  };

  const convertPrice = (priceUSD) => {
    if (currency === 'USD') return priceUSD;
    return priceUSD * exchangeRate;
  };

  const formatPrice = (priceUSD) => {
    const price = convertPrice(priceUSD);
    if (currency === 'USD') {
      return `$${price.toFixed(2)}`;
    }
    return `₲${price.toLocaleString('es-PY', { maximumFractionDigits: 0 })}`;
  };

  const formatUSD = (amount) => {
    return `$${Number(amount || 0).toFixed(2)}`;
  };

  const formatPYG = (amount) => {
    return `₲${Number(amount || 0).toLocaleString('es-PY', { maximumFractionDigits: 0 })}`;
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      <CurrencyContext.Provider value={{ currency, exchangeRate, toggleCurrency, convertPrice, formatPrice, formatUSD, formatPYG, setExchangeRate, fetchExchangeRate }}>
        <BrowserRouter>
          <Toaster position="top-center" />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={
              <ProtectedRoute>
                <AppShell>
                  <Dashboard />
                </AppShell>
              </ProtectedRoute>
            } />
            <Route path="/products" element={
              <ProtectedRoute>
                <AppShell>
                  <ProductsPage />
                </AppShell>
              </ProtectedRoute>
            } />
            <Route path="/purchase-orders" element={
              <ProtectedRoute>
                <AppShell>
                  <PurchaseOrdersPage />
                </AppShell>
              </ProtectedRoute>
            } />
            <Route path="/sales" element={
              <ProtectedRoute>
                <AppShell>
                  <SalesPage />
                </AppShell>
              </ProtectedRoute>
            } />
            <Route path="/invoices" element={
              <ProtectedRoute>
                <AppShell>
                  <InvoicesPage />
                </AppShell>
              </ProtectedRoute>
            } />
            <Route path="/returns-inspection" element={
              <ProtectedRoute>
                <AppShell>
                  <ReturnsInspectionPage />
                </AppShell>
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute>
                <AppShell>
                  <ReportsPage />
                </AppShell>
              </ProtectedRoute>
            } />
            <Route path="/fixed-expenses" element={
              <ProtectedRoute>
                <AppShell>
                  <FixedExpensesPage />
                </AppShell>
              </ProtectedRoute>
            } />
            <Route path="/users" element={
              <ProtectedRoute>
                <AppShell>
                  <UsersPage />
                </AppShell>
              </ProtectedRoute>
            } />
            <Route path="/clients" element={
              <ProtectedRoute>
                <AppShell>
                  <ClientsPage />
                </AppShell>
              </ProtectedRoute>
            } />
            <Route path="/credit-sales" element={
              <ProtectedRoute>
                <AppShell>
                  <CreditSalesPage />
                </AppShell>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <AppShell>
                  <SettingsPage />
                </AppShell>
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </CurrencyContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
